import { toRaw } from 'vue';
import {
    addNode,
    findNode,
    moveNode,
    removeNode,
    removeObjectRefs,
    removeWire,
    replaceNode,
    setNodeData,
    updateNodeData,
} from '../graph/graphModel.js';
import { diffGraphs, isEmptyGraphDiff } from '../graph/graphDiff.js';
import { arrangeGraph } from '../graph/graphLayout.js';
import { resolveNodePorts } from '../Hasp/properties/v2/graphPorts.js';
import { useUndoStore } from '../history/undoStore.js';
import { useCanvasModeStore } from './canvasModeStore.js';

/**
 * The node's wires on ports it no longer has — after a type change, or a data
 * change for ports that follow the data (see resolveNodePorts) — removed.
 */
function withoutStaleWires(domain, graph, id) {
    const ports = resolveNodePorts(domain.getNodeSchema, findNode(graph, id));
    const fits = (end, list) => end.node !== id || list.some(port => port.id === end.port);
    const wires = graph.wires.filter(wire => fits(wire.to, ports.inputs) && fits(wire.from, ports.outputs));
    return wires.length === graph.wires.length ? graph : { ...graph, wires };
}

/**
 * Pinia options for the editor store of one canvas graph domain (Logic,
 * Home Assistant): the only place that mutates `page[domain.graphKey]` on
 * behalf of the user. Every mutation goes through history as the domain's
 * graph command.
 *
 * The graph itself lives on the page (design data); the store only holds
 * editor state: selection and a revision counter for renderers. Visibility
 * is the canvas mode: the graph shows while `domain.mode` is active.
 * Pages reach the store through Vue-reactive parents, so every action
 * unwraps the page first: the graph must stay plain data (structuredClone
 * in diffs).
 *
 * @param {object} domain
 * @param {string} domain.graphKey    page field holding the graph
 * @param {string} domain.mode        canvas mode that shows the graph
 * @param {string} domain.noun        used in history descriptions ("Connect <noun>")
 * @param {() => object} domain.createGraph
 * @param {(type, { x, y, preset, id }) => object} domain.createNode
 * @param {(type) => object|null} domain.getNodeSchema
 * @param {() => object[]} domain.getPalette
 * @param {(graph, entry, placement) => { position, connectFrom, inputPort? }} domain.planPaletteNode
 * @param {(graph, from, to, context) => { graph, wire } | { graph, error }} domain.connect
 * @param {(page) => object} domain.createContext
 * @param {({ pageUuid, diff, description, mergeKey }) => object} domain.createCommand
 * @param {{ state?, getters?, actions? }} [extra] domain-specific store additions
 */
export function graphStoreOptions(domain, extra = {}) {
    const noun = domain.noun;
    const graphOf = page => page[domain.graphKey] ?? domain.createGraph();
    return {
        state: () => ({
            // { pageUuid, kind: 'node' | 'wire', id } | null
            selection: null,
            revision: 0,
            ...extra.state?.(),
        }),

        getters: {
            palette: () => domain.getPalette(),
            visible: () => useCanvasModeStore().mode === domain.mode,
            ...extra.getters,
        },

        actions: {
            /** Enter this domain's mode, or leave it for design; another active mode is kept. */
            setVisible(visible) {
                const canvas = useCanvasModeStore();
                if (visible) canvas.setMode(domain.mode);
                else if (canvas.mode === domain.mode) canvas.setMode('design');
                if (!this.visible) this.selection = null;
            },

            select(page, kind, id) {
                page = toRaw(page);
                this.selection = page && id ? { pageUuid: page.id(), kind, id } : null;
            },

            clearSelection() {
                this.selection = null;
            },

            /**
             * Apply `nextGraph` to `page` and return the history command for it,
             * without pushing it — for callers that batch it with other commands.
             * Returns null when nothing changed.
             */
            record(page, nextGraph, description, { mergeKey = null } = {}) {
                page = toRaw(page);
                const diff = diffGraphs(page[domain.graphKey], nextGraph);
                if (isEmptyGraphDiff(diff)) return null;
                page[domain.graphKey] = nextGraph;
                this.revision++;
                return domain.createCommand({ pageUuid: page.id(), diff, description, mergeKey });
            },

            /** Apply `nextGraph` to `page` and push it onto the undo stack. */
            commit(page, nextGraph, description, options) {
                page = toRaw(page);
                const command = this.record(page, nextGraph, description, options);
                if (command) useUndoStore().push(command);
                return command;
            },

            addNode(page, entry, position = { x: 0, y: 0 }) {
                page = toRaw(page);
                const node = domain.createNode(entry.nodeType, { ...position, preset: entry.preset });
                this.commit(page, addNode(graphOf(page), node), `Add ${entry.label}`);
                this.select(page, 'node', node.id);
                return node;
            },

            /**
             * Add a palette node (see the domain's planPaletteNode for
             * `placement`) and, when the plan names one, connect it — as a
             * single history entry. The plan's `connectFrom` feeds the new
             * node's input (`inputPort`, default the first); a `connectTo`
             * ({ node, port } or { object, port }, from the plan or from
             * `placement`) takes the new node's output (`outputPort`, default
             * the first).
             * @returns {{ node, error: string|null }} error when the connection was refused
             */
            addPaletteNode(page, entry, placement = {}) {
                page = toRaw(page);
                const base = graphOf(page);
                const plan = domain.planPaletteNode(base, entry, placement);
                const node = domain.createNode(entry.nodeType, { ...plan.position, preset: entry.preset });
                const ports = resolveNodePorts(domain.getNodeSchema, node);
                let graph = addNode(base, node);
                let error = null;
                if (plan.connectFrom) {
                    const to = { node: node.id, port: plan.inputPort ?? ports.inputs[0]?.id };
                    const result = domain.connect(graph, plan.connectFrom, to, domain.createContext(page));
                    if (result.error) error = result.error;
                    else graph = result.graph;
                } else if (plan.connectTo ?? placement.connectTo) {
                    // The new node feeds an existing input (e.g. a wire drawn backwards from it).
                    const output = plan.outputPort ? ports.outputs.find(port => port.id === plan.outputPort) : ports.outputs[0];
                    const result = output
                        ? domain.connect(graph, { node: node.id, port: output.id }, plan.connectTo ?? placement.connectTo, domain.createContext(page))
                        : { error: 'This node has no output' };
                    if (result.error) error = result.error;
                    else graph = result.graph;
                }
                this.commit(page, graph, `Add ${entry.label}`);
                this.setVisible(true);
                this.select(page, 'node', node.id);
                return { node, error };
            },

            /** @returns {{ wire } | { error }} */
            connect(page, from, to) {
                page = toRaw(page);
                const result = domain.connect(graphOf(page), from, to, domain.createContext(page));
                if (result.error) return { error: result.error };
                this.commit(page, result.graph, `Connect ${noun}`);
                return { wire: result.wire };
            },

            moveNode(page, id, x, y) {
                page = toRaw(page);
                this.commit(page, moveNode(page[domain.graphKey], id, x, y), `Move ${noun} node`);
            },

            /** Re-place every node of the page around the plate (see arrangeGraph); null when nothing moved. */
            arrange(page, layout) {
                page = toRaw(page);
                const graph = page?.[domain.graphKey];
                return graph ? this.commit(page, arrangeGraph(graph, layout), `Arrange ${noun} nodes`) : null;
            },

            updateNodeData(page, id, patch) {
                page = toRaw(page);
                const graph = withoutStaleWires(domain, updateNodeData(page[domain.graphKey], id, patch), id);
                this.commit(page, graph, `Edit ${noun} node`, { mergeKey: `data:${id}` });
            },

            /** Replace a node's data; consecutive edits of one node form one history entry. */
            setNodeData(page, id, data) {
                page = toRaw(page);
                const graph = withoutStaleWires(domain, setNodeData(page[domain.graphKey], id, data), id);
                this.commit(page, graph, `Edit ${noun} node`, { mergeKey: `data:${id}` });
            },

            /**
             * Switch a node to another variant of its palette category: new
             * type and fresh data (plus `keep`, data carried over), same id,
             * position and wires — except wires on ports the new type lacks.
             */
            setNodeVariant(page, id, variant, keep = {}) {
                page = toRaw(page);
                const node = findNode(page[domain.graphKey], id);
                if (!node || !variant) return;
                const next = domain.createNode(variant.nodeType, { id, x: node.x, y: node.y, preset: { ...variant.preset, ...keep } });
                this.commit(page, withoutStaleWires(domain, replaceNode(page[domain.graphKey], next), id), `Change to ${variant.label}`);
            },

            removeWire(page, wireId) {
                page = toRaw(page);
                if (this.selection?.kind === 'wire' && this.selection.id === wireId) this.selection = null;
                this.commit(page, removeWire(page[domain.graphKey], wireId), `Delete ${noun} wire`);
            },

            deleteSelection(page) {
                page = toRaw(page);
                const selection = this.selection;
                const graph = page?.[domain.graphKey];
                if (!selection || !graph || selection.pageUuid !== page.id()) return null;
                const next = selection.kind === 'wire'
                    ? removeWire(graph, selection.id)
                    : findNode(graph, selection.id) ? removeNode(graph, selection.id) : graph;
                this.selection = null;
                return this.commit(page, next, selection.kind === 'wire' ? `Delete ${noun} wire` : `Delete ${noun} node`);
            },

            /**
             * Remove the wires of UI objects that are about to be deleted.
             * Returns the (unpushed) history commands, one per affected page,
             * so the caller can batch them with the object deletion.
             */
            detachObjects(objects, getPage) {
                const byPage = new Map();
                for (const object of objects) {
                    const page = toRaw(getPage(object));
                    if (!page?.[domain.graphKey]) continue;
                    const uuids = byPage.get(page) ?? new Set();
                    for (const node of [object, ...(object.find?.(child => typeof child.obj === 'string') ?? [])]) {
                        uuids.add(node.id());
                    }
                    byPage.set(page, uuids);
                }
                return [...byPage].map(([page, uuids]) => this.record(
                    page, [...uuids].reduce(removeObjectRefs, page[domain.graphKey]), `Detach ${noun}`,
                )).filter(Boolean);
            },

            ...extra.actions,
        },
    };
}
